import React from 'react';
import { Configure } from 'react-instantsearch';
import Box from '../Atoms/Box';
import AlgoliaTableHeader from './TableHeader';
import AlgoliaTableRows from './TableRows';
import { PageSizeOption } from './types';
import { TableHeaderTypes } from '../Atoms/Table/Types';

// Define shimmer animation keyframes
const shimmerAnimation = {
  '@keyframes shimmer': {
    '0%': {
      backgroundPosition: '-1000px 0',
    },
    '100%': {
      backgroundPosition: '1000px 0',
    },
  },
};

interface TableContextProps {
  tableTitle: string;
  tableColumns: TableHeaderTypes[];
  showCheckbox: boolean;
  selectedQuestions: string[];
  selectedTagColumns: string[];
  currentPage: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  handlePageSizeChange: (value: string | number | object) => void;
  handleQuestionSelect: (questionId: string, selected: boolean) => void;
  handleTagsOpen: (hit: any) => void;
  pageSizeOptions: PageSizeOption[];
  facetFilters: string[][];
  onConfigureColumns: () => void;
  emptyStateComponent?: React.ReactNode;
  questionSource?: string;
  selectButton?: (referenceId: string) => React.ReactNode;
}

/**
 * Component that provides table elements within the shared Algolia context
 * (No longer creates its own InstantSearch - uses shared context)
 */
const TableContext: React.FC<TableContextProps> = ({
  tableTitle,
  tableColumns,
  showCheckbox,
  selectedQuestions,
  selectedTagColumns,
  currentPage,
  setCurrentPage,
  pageSize,
  handlePageSizeChange,
  handleQuestionSelect,
  handleTagsOpen,
  pageSizeOptions,
  facetFilters,
  onConfigureColumns,
  emptyStateComponent,
  questionSource,
  selectButton = undefined,
}) => (
  <>
    <Configure
      key={`filters-${JSON.stringify(facetFilters)}-${pageSize}-${currentPage}-${
        questionSource || 'default'
      }`}
      hitsPerPage={pageSize}
      page={currentPage}
      facetFilters={facetFilters}
      attributesToRetrieve={['*']}
      distinct={false}
    />
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        position: 'relative',
        transition: 'all 0.3s ease-in-out',
        '@keyframes shimmer': shimmerAnimation['@keyframes shimmer'],
      }}
    >
      <AlgoliaTableHeader
        tableTitle={tableTitle}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        onPageSizeChange={handlePageSizeChange}
        onConfigureColumns={onConfigureColumns}
      />
      <AlgoliaTableRows
        tableColumns={tableColumns}
        showCheckbox={showCheckbox}
        selectedQuestions={selectedQuestions}
        selectedTagColumns={selectedTagColumns}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        pageSize={pageSize}
        handleQuestionSelect={handleQuestionSelect}
        handleTagsOpen={handleTagsOpen}
        emptyStateComponent={emptyStateComponent}
        selectButton={selectButton}
      />
    </Box>
  </>
);

export default TableContext;
